<template>
  <div class="mb-3 pb-4 border-bottom">
    <div class="d-flex align-items-center mb-3 flex-wrap">
      <h4 class="mb-0 mr-3">{{ itemIndex + 1 }}. Pax to be updated</h4>

      <div
        :class="{ 'form-control': true, 'is-select': true, 'is-invalid': !matchedPax }"
        style="width: 250px;">
        <v-select
          :options="computedPassengers"
          v-model="matchedPax"
          :getOptionLabel="option => `${option.first_name} ${option.last_name}`"
          placeholder="Search / select a passenger..."
          :filterable="false"
          :clearable="false"
          @search="onSearch"
          @open="onDropdownOpen"
          append-to-body>

          <template slot="no-options">
            <template v-if="isLoading || isSearching">Searching in the system&hellip;</template>
            <template v-else>No results to display&hellip;</template>
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center justify-content-between w-100">
              <div>
                {{ option.first_name }} {{ option.last_name }}
                <template v-if="option.passports_count > 1">({{ option.passports_count }})</template>
              </div>
              <div v-if="scannedMatchedPaxId && option.id === scannedMatchedPaxId" class="badge badge-primary ml-2">
                Matched
              </div>
            </div>
          </template>

          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.first_name }} {{ option.last_name }}
            </div>
          </template>
        </v-select>
        <div
          v-if="!matchedPax"
          class="invalid-feedback">
          Please select PAX
        </div>
      </div>

      <div
        v-if="!isLoading && matchedPax"
        class="ml-3 position-relative custom-tooltip-container">
        <div class="custom-tooltip-icon">?</div>
        <div class="custom-tooltip-box shadow-sm border">
          The Origin data table contains the passenger's passport data saved in the application. Select Add new to add the imported data as a new passport in the Imported data for update table, or select an existing passport to update it.
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <div class="mt-2">Loading passenger data from system&hellip;</div>
    </div>

    <d-alert
      v-else-if="isUpdated"
      theme="success"
      show
      class="my-0">
      Passport data successfully updated for:
      <strong>{{ scannedItem.passenger.first_name }} {{ scannedItem.passenger.last_name }}
      (DOB: {{ getDateString(scannedItem.passenger.birth_date) }})</strong>.
    </d-alert>

    <template v-else>
      <d-alert
        v-if="!matchedPax"
        theme="danger"
        show
        class="mb-4">
        No passenger was found in the system for:
        <strong>{{ scannedItem.passenger.first_name }} {{ scannedItem.passenger.last_name }}
        (DOB: {{ getDateString(scannedItem.passenger.birth_date) }})</strong>
      </d-alert>

      <div class="mb-4">
        <h5 class="">Origin data</h5>
        <div class="table-wrapper">
          <table class="table table-bordered table-sm m-0">
            <thead class="bg-light text-muted">
              <tr>
                <th class="align-middle">First name</th>
                <th class="align-middle">Last name</th>
                <th class="align-middle">DOB</th>
                <th class="align-middle">Sex</th>
                <th class="align-middle">Customer</th>
                <th class="align-middle" style="width: 180px;">Passport name</th>
                <th class="align-middle" style="width: 80px;">Nationality</th>
                <th class="align-middle" style="min-width: 220px;">Country</th>
                <th class="align-middle">Expiry</th>
                <th class="align-middle">Is default</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="align-middle text-muted">
                  {{ displayPax.first_name || '-' }}
                </td>
                <td class="align-middle text-muted">
                  {{ displayPax.last_name || '-' }}
                </td>
                <td class="align-middle text-muted">
                  {{ getDateString(displayPax.birth_date) }}
                </td>
                <td class="align-middle text-muted">
                  {{ displayPax.sex === 'M' ? 'Male' : (displayPax.sex === 'F' ? 'Female' : '-') }}
                </td>
                <td class="align-middle text-muted">
                  {{ getCustomerName(displayPax.flight_customer_id) || '-' }}
                </td>

                <td class="align-middle">
                  <div class="form-control is-select">
                    <v-select
                      :options="passportOptions"
                      v-model="selectedPassportId"
                      :reduce="opt => opt.value"
                      label="label"
                      :clearable="false"
                      :disabled="!matchedPax"
                      append-to-body>
                      <template
                        slot="option"
                        slot-scope="option">
                        <span :class="{'text-primary font-weight-bold': option.value === null}">
                          <i
                            v-if="option.value === null"
                            class="material-icons"
                            style="font-size: 14px; vertical-align: text-bottom;">add</i>
                          {{ option.label }}
                        </span>
                      </template>
                    </v-select>
                  </div>
                </td>

                <td class="align-middle text-muted">
                  {{ getCountryIso(selectedOriginPassport.country_id) }}
                </td>
                <td class="align-middle text-muted">
                  <span
                    v-for="(countryItem, index) in selectedOriginPassport.countries"
                    :key="index"
                    class="badge badge-light border mr-1">
                    {{ getCountryText(countryItem.id || countryItem) }}
                  </span>
                </td>
                <td class="align-middle text-muted">
                  {{ selectedOriginPassport.expiry }}
                </td>
                <td class="align-middle text-muted">
                  <span v-if="selectedOriginPassport.id">
                    {{ selectedOriginPassport.is_default ? 'Yes' : 'No' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mb-2">
        <h5 class="">Imported data for update</h5>
        <div class="table-wrapper pb-3">
          <validation-observer ref="pax-update-form" tag="div">
            <table class="table table-bordered table-sm m-0">
              <thead class="bg-light text-muted">
                <tr>
                  <th class="align-middle">First name</th>
                  <th class="align-middle">Last name</th>
                  <th class="align-middle">DOB</th>
                  <th class="align-middle">Sex</th>
                  <th class="align-middle">Customer</th>
                  <th class="align-middle" style="width: 180px;">Passport name</th>
                  <th class="align-middle" style="width: 80px;">Nationality</th>
                  <th class="align-middle" style="min-width: 220px;">Country</th>
                  <th class="align-middle">Expiry</th>
                  <th class="align-middle">Set as default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="align-middle text-muted">
                    {{ scannedItem.passenger.first_name }}
                  </td>
                  <td class="align-middle text-muted">
                    {{ scannedItem.passenger.last_name }}
                  </td>
                  <td class="align-middle text-muted">
                    {{ getDateString(scannedItem.passenger.birth_date) }}
                  </td>
                  <td class="align-middle text-muted">
                    {{ scannedItem.passenger.sex === 'M' ? 'Male' : (scannedItem.passenger.sex === 'F' ? 'Female' : '-') }}
                  </td>
                  <td class="align-middle text-muted">
                    {{ getCustomerName(displayPax.flight_customer_id) || '-' }}
                  </td>

                  <td>
                    <validation-provider
                      rules="required"
                      name="passport_name"
                      v-slot="{ errors }">
                      <input
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.length }"
                        v-model="editablePassport.name"
                        :disabled="selectedPassportId !== null || isSubmitting || !matchedPax">
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td>
                    <div class="form-control is-select">
                      <v-select
                        :options="countriesOptions"
                        v-model="editablePassport.country_id"
                        :reduce="country => country.id"
                        placeholder="Select a nationality"
                        :multiple="false"
                        label="text"
                        :disabled="isSubmitting || !matchedPax"
                        append-to-body>
                        <template
                          slot="option"
                          slot-scope="option">
                          <div class="d-center">{{ option.text }}</div>
                        </template>
                        <template
                          slot="selected-option"
                          slot-scope="option">
                          <div class="selected d-center">{{ option.iso_a3 || option.text }}</div>
                        </template>
                        <template slot="no-options">
                          There are no nationalities for the provided phrase&hellip;
                        </template>
                      </v-select>
                    </div>
                  </td>
                  <td>
                    <validation-provider
                      rules="required"
                      name="passport_countries"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control form-control-multiple': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          :options="countriesOptions"
                          v-model="editablePassport.countries"
                          :reduce="country => country.id"
                          placeholder="Select a country"
                          :multiple="true"
                          label="text"
                          :disabled="isSubmitting || !matchedPax"
                          append-to-body>
                          <template slot="no-options">
                            There are no countries for the provided phrase&hellip;
                          </template>
                        </v-select>
                      </div>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td>
                    <validation-provider
                      rules="required"
                      name="passport_expiry"
                      v-slot="{ errors }">
                      <input
                        type="date"
                        lang="en-US"
                        class="form-control native-date-input"
                        :class="{ 'is-invalid': errors.length, 'has-value': !!editablePassport.expiry }"
                        v-model="nativeExpiry"
                        :disabled="isSubmitting || !matchedPax">
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td>
                    <div class="form-control is-select">
                      <v-select
                        :options="isDefaultOptions"
                        v-model="editablePassport.is_default"
                        :reduce="item => item.value"
                        label="label"
                        :clearable="false"
                        :disabled="isSubmitting || !matchedPax"
                        append-to-body>
                      </v-select>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </validation-observer>
        </div>
      </div>

      <div class="text-right">
        <d-button
          theme="info"
          :disabled="isSubmitting || !matchedPax"
          @click.prevent="submitItem">
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-1" role="status"></span>
          {{ isSubmitting ? 'Updating...' : 'Update Pax' }}
        </d-button>
      </div>
    </template>
  </div>
</template>

<script>
import FormUtils from '@/utils/FormUtils';
import DateUtils from '@/utils/DateUtils.js';

export default {
  name: 'pax-update-item',
  props: {
    countriesOptions: {
      default: () => [],
      type: Array
    },
    itemIndex: {
      required: true,
      type: Number
    },
    operators: {
      default: () => [],
      type: Array
    },
    scannedItem: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      editablePassport: {
        countries: [],
        country_id: null,
        expiry: null,
        is_default: 0,
        name: '',
        serial_number: ''
      },
      isDefaultOptions: [{
        label: 'Yes',
        value: 1
      }, {
        label: 'No',
        value: 0
      }],
      isLoading: true,
      isSubmitting: false,
      isUpdated: false,
      isSearching: false,
      matchedPax: null,
      scannedMatchedPaxId: null,
      selectedPassportId: null,
      passengers: [],
      loadedPassengers: false,
      searchTimeout: null,
    };
  },
  computed: {
    displayPax() {
      return this.matchedPax || {};
    },
    computedPassengers() {
      let list = [...this.passengers];

      if (this.matchedPax) {
        list = list.filter(p => p.id !== this.matchedPax.id);
        list.unshift(this.matchedPax);
      }

      return list;
    },
    passportOptions () {
      if (!this.matchedPax) {
        return [{ label: 'Add new', value: null }];
      }

      let opts = (this.matchedPax.passports || []).map((item, index) => ({
        label: item.name || `Passport ${index + 1}`,
        value: item.id,
        data: item
      }));

      opts.push({ label: 'Add new', value: null, data: null });
      return opts;
    },
    selectedOriginPassport () {
      if (this.selectedPassportId === null) {
        return {};
      }
      let found = this.passportOptions.find(opt => opt.value === this.selectedPassportId);
      return found ? (found.data || {}) : {};
    },
    nativeExpiry: {
      get () {
        if (!this.editablePassport.expiry) {
          return '';
        }

        const expiryDate = new Date(this.editablePassport.expiry);

        if (expiryDate.toString() === 'Invalid Date') {
          return '';
        }

        const year = expiryDate.getFullYear();
        const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
        const day = String(expiryDate.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      },
      set (val) {
        if (!val) {
          this.editablePassport.expiry = null;
          return;
        }
        this.editablePassport.expiry = new Date(val);
      }
    }
  },
  watch: {
    selectedPassportId (newVal) {
      if (newVal !== null && this.selectedOriginPassport) {
        this.editablePassport.name = this.selectedOriginPassport.name;
      } else {
        this.editablePassport.name = 'Passport New';
      }
    },
    matchedPax (newVal, oldVal) {
      if ((oldVal && newVal && oldVal.id !== newVal.id) || !newVal) {
        this.selectedPassportId = null;
      }
    }
  },
  async mounted () {
    this.editablePassport = {
      ...this.scannedItem.passport,
      name: 'Passport New'
    };

    if (this.scannedItem.passport.expiry) {
      this.editablePassport.expiry = new Date(this.scannedItem.passport.expiry);
    }

    await this.searchPaxInSystem();
  },
  methods: {
    async searchPaxInSystem () {
      this.isLoading = true;

      try {
        const response = await this.$http.post(
          '/passengers/search',
          {
            first_name: this.scannedItem.passenger.first_name,
            last_name: this.scannedItem.passenger.last_name,
            birth_date: DateUtils.convertDateToString(this.scannedItem.passenger.birth_date)
          }
        );

        if (!response || !response.status || response.status !== 200 || !response.data) {
          throw new Error('Passenger search request failed');
        }

        if (response.data.success === 0 || !response.data.data) {
          throw new Error('Passenger not found');
        }

        const paxData = response.data.data;
        let rawGender = paxData.sex;

        if (rawGender) {
          let normalizedGender = rawGender.trim().toLowerCase();
          if (normalizedGender === 'female' || normalizedGender === 'f') {
            paxData.sex = 'F';
          } else if (normalizedGender === 'male' || normalizedGender === 'm') {
            paxData.sex = 'M';
          }
        }

        this.matchedPax = paxData;
        this.scannedMatchedPaxId = paxData.id;
      } catch (error) {
        console.warn('Initial passenger match not found', error);
        this.matchedPax = null;
      } finally {
        this.isLoading = false;
      }
    },
    onDropdownOpen () {
      if (!this.loadedPassengers) {
        this.loadPassengers();
      }
    },
    loadPassengers (searchQuery = '') {
      const where = {};

      if (searchQuery) {
        where.search = searchQuery;
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/passengers/items',
        params: {
          pagination: 1,
          per_page: 10,
          where
        },
        loadedKey: 'loadedPassengers',
        outputKey: 'passengers',
        errorKey: 'loadError'
      });
    },
    onSearch (search, loading) {
      if (search.length) {
        loading(true);
        this.isSearching = true;
        this.searchDebounced(search, loading);
      } else {
        this.loadPassengers();
      }
    },
    searchDebounced (search, loading) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadPassengers(search);
        loading(false);
        this.isSearching = false;
      }, 400);
    },
    getCustomerName (id) {
      if (!id) {
        return '';
      }
      let customer = this.operators.find(item => item.id === id);
      return customer ? customer.name : '';
    },
    getCountryText (id) {
      if (!id) {
        return '';
      }
      let country = this.countriesOptions.find(item => item.id === id);
      return country ? (country.iso_a3 || country.text) : '';
    },
    getCountryIso (id) {
      let country = this.countriesOptions.find(item => item.id === id);
      return country ? (country.iso_a3 || country.text) : id;
    },
    getDateString (date) {
      if (date) {
        return DateUtils.convertDateToString(date);
      }

      return '-';
    },
    async submitItem () {
      if (!this.matchedPax) {
        return;
      }

      const isValid = await this.$refs['pax-update-form'].validate();

      if (!isValid) {
        this.$nextTick(() => {
          const firstErrorElement = this.$el.querySelector('.is-invalid');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }

      this.isSubmitting = true;

      let endpointUrl = this.selectedPassportId === null
        ? '/passports/store'
        : '/passports/update/' + this.selectedPassportId;

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: {
          reference_id: this.matchedPax.id,
          reference_type: 'PASSENGER',
          name: this.editablePassport.name,
          serial_number: this.editablePassport.serial_number,
          country_id: this.editablePassport.country_id || 0,
          countries: this.editablePassport.countries,
          expiry: DateUtils.convertDateToString(this.editablePassport.expiry),
          is_default: this.editablePassport.is_default
        },
        successTitle: 'Update successful',
        successText: 'The passport data has been updated.',
        errorTitle: 'An error occurred',
        errorText: 'Failed to save the update.',
        successAction: () => {
          this.isSubmitting = false;
          this.isUpdated = true;
        },
        failAction: () => {
          this.isUpdated = false;
          this.isSubmitting = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

.table thead th:not(.is-sticky) {
  min-width: 170px;
}

.pb-3.table-wrapper {
  padding-bottom: 250px;
}

.table-sm th, .table-sm td {
  padding: 0.5rem;
  vertical-align: middle;
}

.custom-tooltip-container {
  display: inline-block;
  cursor: help;
  z-index: 10;

  &:hover .custom-tooltip-box {
    display: block;
  }
}

.custom-tooltip-icon {
  width: 24px;
  height: 24px;
  background-color: #f0ad4e;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
}

.custom-tooltip-box {
  display: none;
  position: absolute;
  top: -10px;
  left: 35px;
  width: 380px;
  background-color: #fff;
  padding: 15px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  line-height: 1.4;

  &:before {
    content: '';
    position: absolute;
    top: 15px;
    left: -6px;
    width: 12px;
    height: 12px;
    background-color: #fff;
    border-left: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    transform: rotate(45deg);
  }
}

/* Ujednolicenie wyglądu i ukrycie brzydkiego, polskiego placeholdera z przeglądarki Chrome/Edge */
.native-date-input {
  position: relative;
  text-transform: uppercase;
  color: #495057;

  &::-webkit-datetime-edit {
    padding: 0;
  }

  /* Stylizacja ikonki kalendarza */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: 0.2s;
    &:hover {
      opacity: 1;
    }
  }

  /* Kiedy input jest pusty, robimy kolor tekstu "przezroczysty" by ukryć dd.mm.rrrr */
  &:not(.has-value) {
    color: transparent;
  }

  /* Ale zostawiamy widoczną ikonkę kalendarza */
  &:not(.has-value)::-webkit-calendar-picker-indicator {
    opacity: 1;
  }
}
</style>
